import { FC, useEffect, useState } from 'react';

import { Button, Image } from '../../atoms';
import { SessionDataMap } from './MiniCard.schema';
import { bannerLinkProps, MiniCardProps } from './MiniCard.types';
import { StyledComp, StyledImageContainer, StyledSection } from './MiniCard.styles';
import cssClass from 'classnames';

import {
  createImageSizes,
  eventUtil,
  getWindowSession,
  canUseDOM,
  transformResponse,
  useScreenWidth,
  useLogoAutomation,
} from '../../utils';
import { constants, TRACKING_CONSTANT, CUSTOM_EVENTS_CONSTANTS } from '../../constants';
import { getMiniCardAttribute, replaceMiniCardWithTIBanner } from './helper';
import { LogoContainer } from '@marriott/mi-ui-library';

const {
  BONVOY_BANNER,
  REDEMPTION_ALERT,
  CHECK_IN_BANNER,
  TRAVEL_INSURANCE_BANNER,
  MAX_VIEWPORT_MOBILE,
  RESCONFO_TI_BANNER,
  SONDER_PAV_BANNER,
  TARGET_BLANK,
  MINICARD,
} = constants;
const { TI_PLACEMENT } = CUSTOM_EVENTS_CONSTANTS;

const BannerLink = ({ ctaLink, type, target, trackingProperties, linkAriaLabel }: bannerLinkProps) => {
  const iconName = type === BONVOY_BANNER ? 'm-link-tertiary-button' : 'icon-arrow-right';
  const isClickableCard = type === MINICARD || type === RESCONFO_TI_BANNER || type === BONVOY_BANNER;
  return ctaLink ? (
    isClickableCard ? (
      <span className={iconName + ' link'} />
    ) : (
      <Button
        isLink={true}
        href={ctaLink}
        className={iconName + ' link'}
        target={target}
        trackingProperties={trackingProperties}
        linkAriaLabel={linkAriaLabel}
      >
        <span>{type}</span>
      </Button>
    )
  ) : null;
};

export const MiniCard: FC<MiniCardProps> = ({
  type,
  target,
  ctaLink,
  altText,
  fileReferenceDynamic,
  cardTitle,
  cardDescription,
  cardCTA,
  linkAriaLabel,
  trackingProperties,
  linkType = '',
  noPwdEnroll,
  tagLine,
  tibannerTitle = '',
  styleclass = '',
}: MiniCardProps) => {
  const [travelInsuranceDeepLink, setTravelInsuranceDeepLink] = useState<string>('');
  const sessionObject = getWindowSession();
  const { isMaxProperty } = transformResponse(sessionObject, SessionDataMap);
  const renditions = fileReferenceDynamic?.renditions;
  const imageSize = createImageSizes(renditions);
  const isredemptionAlert = type === REDEMPTION_ALERT;
  const isSonderBrand = type === SONDER_PAV_BANNER;
  const isCtaRequired = type === CHECK_IN_BANNER || type === TRAVEL_INSURANCE_BANNER || noPwdEnroll || isSonderBrand;
  const isMobileViewPort = useScreenWidth() < MAX_VIEWPORT_MOBILE;
  const isLogoFlagEnabled = useLogoAutomation();
  const isClickableCard = type === MINICARD || type === RESCONFO_TI_BANNER;
  const btnSectionClassName = cssClass({
    'standard m-button-m button': type === CHECK_IN_BANNER || isSonderBrand,
    'standard m-button-s button': noPwdEnroll,
    'm-button-secondary button': type === TRAVEL_INSURANCE_BANNER,
  });

  const titleWrapper = cssClass('title-wrapper', {
    'ml-5': type === TRAVEL_INSURANCE_BANNER && !isMobileViewPort,
    'wo-pwd': noPwdEnroll,
  });

  const compClassName = cssClass(`${type} ${styleclass}`, {
    'no-pwd-view': noPwdEnroll,
    'd-none': type === RESCONFO_TI_BANNER && !travelInsuranceDeepLink,
    'banner-section custom_click_track': isClickableCard,
  });

  useEffect(() => {
    if (type === RESCONFO_TI_BANNER) {
      // code to hide the minicard and show the TI banner if TI deep link is there
      if (travelInsuranceDeepLink) replaceMiniCardWithTIBanner();
      // event listener to listen TI event to get deeplink
      if (!travelInsuranceDeepLink) {
        canUseDOM && eventUtil.on(TI_PLACEMENT, data => data?.deepLink && setTravelInsuranceDeepLink(data?.deepLink));
      }
    }

    return () => eventUtil.remove(TI_PLACEMENT, _e => setTravelInsuranceDeepLink(''));
  }, [travelInsuranceDeepLink, type]);

  const handleAction: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void = event => {
    // If it's a keyboard event, only proceed if the key is 'Enter'
    if (event && 'key' in event) {
      if (event.key !== 'Enter') return;
      event.preventDefault(); // Prevent default behavior for Enter
    }

    const url = !noPwdEnroll
      ? getMiniCardAttribute(type, cardCTA?.ctaLink, ctaLink, travelInsuranceDeepLink)
      : cardCTA?.ctaLink;
    const target = !noPwdEnroll ? getMiniCardAttribute(type, cardCTA?.target, cardCTA?.target) : cardCTA?.target;

    if (url) {
      if (target === '_self') {
        window.location.href = url;
      } else {
        window.open(url, target || '_blank');
      }
    }
  };

  const brandSNClassName = cssClass('brand-logo-SN t-background-color color-scheme7 t-brand-logo-s sn-brand-logo');

  const cardProps = {
    'data-component-name': 'm-book-MiniCard',
    'data-testid': 'book-MiniCard',
    className: compClassName,
    id: type,
    ...(isClickableCard && {
      tabIndex: 0,
      onKeyDown: handleAction,
      role: 'link',
      onClick: handleAction,
    }),
  };
  const renderSondarLogo = () => {
    return isLogoFlagEnabled && !isMobileViewPort ? (
      <LogoContainer
        brand={'SN'}
        width={72}
        height={72}
        aspectRatio={'3:2'}
        alignment={'Centered'}
        isLink={false}
        color={'background-aware'}
      />
    ) : (
      <span className={brandSNClassName} role="img" aria-label={fileReferenceDynamic?.altText}></span>
    );
  };
  const renderContent = () => {
    return (
      <StyledComp {...cardProps}>
        <StyledSection className="flex-1">
          {isredemptionAlert ? (
            <span className="icon-free-nights icon-extra-free-nights"></span>
          ) : isSonderBrand ? (
            renderSondarLogo()
          ) : (
            <StyledImageContainer className={type}>
              <Image
                url={fileReferenceDynamic?.damPath}
                renditions={fileReferenceDynamic?.renditions}
                dynamic={fileReferenceDynamic?.dynamic}
                title={altText}
                size={imageSize}
                appliedClass={'banner-image'}
              />
            </StyledImageContainer>
          )}
          <div className={titleWrapper}>
            {noPwdEnroll && <p className="tagline">{tagLine}</p>}
            {cardTitle && <h2 className="t-title-xs heading standard">{cardTitle}</h2>}
            {tibannerTitle && <h2 className="t-title-xs heading standard">{tibannerTitle}</h2>}
            <p className="description standard">{cardDescription}</p>
          </div>
        </StyledSection>
        {isCtaRequired && (
          <div className="btn-section">
            <Button
              isLink={true}
              href={cardCTA?.ctaLink}
              className={btnSectionClassName}
              target={cardCTA?.target}
              trackingProperties={cardCTA?.trackingProperties}
              linkAriaLabel={cardCTA?.ctaText}
              linkType={isSonderBrand ? TRACKING_CONSTANT.EXTERNAL_LINK : linkType}
              externalArrow={cardCTA?.target?.includes(TARGET_BLANK)}
            >
              {cardCTA?.ctaText}
            </Button>
          </div>
        )}
        {!noPwdEnroll ? (
          <BannerLink
            type={type}
            ctaLink={getMiniCardAttribute(type, cardCTA?.ctaLink, ctaLink, travelInsuranceDeepLink)}
            target={getMiniCardAttribute(type, cardCTA?.target, target)}
            trackingProperties={cardCTA?.trackingProperties ?? trackingProperties}
            linkAriaLabel={linkAriaLabel}
          />
        ) : null}
      </StyledComp>
    );
  };

  if (isMaxProperty && type === BONVOY_BANNER) return null;

  return type === BONVOY_BANNER ? (
    <Button
      isLink={true}
      buttonClassName={'d-block'}
      href={cardCTA?.ctaLink}
      target={cardCTA?.target}
      trackingProperties={cardCTA?.trackingProperties ?? trackingProperties}
      externalArrow={false}
      linkType={linkType || TRACKING_CONSTANT.INTERNAL_LINK}
    >
      {renderContent()}
    </Button>
  ) : (
    renderContent()
  );
};
